import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wine, Beer, Coffee, Baby, Mountain, Music, MapPin, ArrowRight, Heart, Navigation, Share2, Map as MapIcon, List } from "lucide-react";
import api from "@/lib/api";
import { MapView } from "@/components/MapView";
import { TRAILS } from "@/lib/trails";

const ICONS = { wine: Wine, coffee: Coffee, baby: Baby, beer: Beer, mountain: Mountain, music: Music };

export default function Trail() {
  const { slug } = useParams();
  const cfg = TRAILS[slug];
  const [stops, setStops] = useState([]);
  const [view, setView] = useState("list");

  const stopDest = (p) => encodeURIComponent(p.address || `${p.name}, ${p.city || ""}, ${p.state || "SC"}`);
  const routeUrl = () => {
    const pts = stops.map(stopDest);
    if (pts.length < 2) return null;
    let u = `https://www.google.com/maps/dir/?api=1&origin=${pts[0]}&destination=${pts[pts.length - 1]}&travelmode=driving`;
    const wp = pts.slice(1, -1).join("|");
    if (wp) u += `&waypoints=${wp}`;
    return u;
  };
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: cfg?.title, text: cfg?.subtitle, url }); return; } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Trail link copied — share it with friends!"); }
    catch { toast.error("Couldn't copy the link"); }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!cfg) return;
    api.get("/places", { params: { limit: 500 } }).then(({ data }) => {
      const matched = data.filter((p) => {
        const hay = `${p.name} ${(p.tags || []).join(" ")} ${p.sub_category || ""}`.toLowerCase();
        return cfg.keywords.some((k) => hay.includes(k));
      });
      const bySlug = Object.fromEntries(matched.map((p) => [p.slug, p]));
      const ordered = cfg.order.map((s) => bySlug[s]).filter(Boolean);
      const rest = matched.filter((p) => !cfg.order.includes(p.slug));
      setStops([...ordered, ...rest].slice(0, 8));
    });
  }, [slug, cfg]);

  if (!cfg) return <Navigate to="/trails" replace />;

  const HeroIcon = ICONS[cfg.icon] || Wine;
  const stopIcon = (p) => {
    if (cfg.altKeywords) {
      const hay = `${p.name} ${(p.tags || []).join(" ")} ${p.sub_category || ""}`.toLowerCase();
      return cfg.altKeywords.some((k) => hay.includes(k)) ? Wine : Beer;
    }
    return HeroIcon;
  };

  return (
    <div className="pb-20" data-testid={`trail-page-${cfg.slug}`}>
      <section className="relative overflow-hidden">
        <img src={cfg.hero} alt={cfg.title} className="h-[46vh] max-h-[420px] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
        <div className="absolute inset-0 mx-auto flex max-w-4xl flex-col justify-end px-4 pb-8 md:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground">
              <HeroIcon size={13} /> {cfg.eyebrow}
            </span>
            <h1 className="font-serif text-4xl font-bold text-white text-shadow-soft sm:text-5xl">{cfg.title}</h1>
            <p className="mt-3 max-w-xl text-base text-white/85 text-shadow-soft md:text-lg">{cfg.subtitle}</p>
          </motion.div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 md:px-8">
        <div className="mt-8 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4">
          <span className="flex items-center gap-2 text-sm font-semibold"><Heart size={15} className="fill-primary text-primary" /> {stops.length} stops</span>
          <span className="text-muted-foreground">·</span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground"><HeroIcon size={15} /> {cfg.metaLeft}</span>
          <span className="text-muted-foreground">·</span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">{cfg.metaRight}</span>
        </div>

        {/* Toolbar: share, open route in Maps, list/map toggle */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button onClick={share} data-testid="trail-share-btn"
            className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold transition-colors hover:border-primary">
            <Share2 size={15} /> Share
          </button>
          {routeUrl() && (
            <a href={routeUrl()} target="_blank" rel="noopener noreferrer" data-testid="trail-open-maps-btn"
              className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold transition-colors hover:border-primary">
              <Navigation size={15} /> Open route in Maps
            </a>
          )}
          <div className="ml-auto flex items-center gap-1 rounded-full border border-border bg-card p-1">
            <button onClick={() => setView("list")} data-testid="trail-view-list"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <List size={14} /> List
            </button>
            <button onClick={() => setView("map")} data-testid="trail-view-map"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${view === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <MapIcon size={14} /> Map
            </button>
          </div>
        </div>

        {stops.length === 0 ? (
          <p className="mt-10 text-center text-muted-foreground">Building your trail…</p>
        ) : view === "map" ? (
          <div className="mt-6" data-testid="trail-map">
            <MapView places={stops} route height={480} />
            <p className="mt-3 text-center text-sm text-muted-foreground">
              Numbered stops follow the route order — tap “Open route in Maps” for turn-by-turn directions.
            </p>
          </div>
        ) : (
          <ol className="relative mt-8 space-y-5 border-l-2 border-dashed border-border pl-6">
            {stops.map((p, i) => {
              const StopIcon = stopIcon(p);
              return (
                <motion.li
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: Math.min(i * 0.05, 0.3) }}
                  className="relative"
                  data-testid={`trail-stop-${i}`}
                >
                  <span className="absolute -left-[35px] flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <Link to={`/place/${p.slug}`} className="card-hover flex gap-4 rounded-2xl border border-border bg-card p-3">
                    <img src={p.cover_image} alt={p.name} className="h-24 w-24 flex-shrink-0 rounded-xl object-cover" />
                    <div className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                        <StopIcon size={12} /> {cfg.slots[i] || "Keep going"}
                      </span>
                      <h3 className="font-serif text-lg font-bold">{p.name}</h3>
                      <p className="line-clamp-1 text-sm text-muted-foreground">{p.short_tagline || p.description}</p>
                      {p.city && (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin size={11} /> {p.city}
                        </p>
                      )}
                    </div>
                  </Link>
                </motion.li>
              );
            })}
          </ol>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to={cfg.exploreLink} data-testid="trail-explore-btn"
            className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
            Explore more spots <ArrowRight size={16} />
          </Link>
          <Link to="/itinerary" data-testid="trail-plan-btn"
            className="flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold transition-colors hover:border-primary">
            <Navigation size={16} /> Build your own day
          </Link>
          <Link to="/trails" data-testid="trail-all-btn"
            className="flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold transition-colors hover:border-primary">
            All trails
          </Link>
        </div>
      </div>
    </div>
  );
}
